import React, { useState, useEffect } from "react";
import SEO from "../seo";
const ManageLandingPageBrandAssets = () => {
  const [activeSection, setActiveSection] = useState("logo");

  useEffect(() => {
    const handleScroll = () => {
      const sections = ["logo", "brand-colors"];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Manage Landing Page Brand Assets – Help Center"
        description="Learn how to upload your logo, configure brand colors, and maintain a consistent brand identity on your Xpage landing page."
        canonical="https://help.xpage.ai/help/landing-page-account/Manage%20Landing%20Page%20Brand%20Assets"
        type="article"
      />

      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Header Section */}
        <header className="mb-6">
          <h1 className="text-systemTitle text-2xl font-semibold mb-3">
            Manage Landing Page Brand Assets
          </h1>
          <p className="text-sm text-systemLabel">
            Establishing a strong brand identity is key to building trust and
            recognition. In Xpage, you can easily manage your landing page's
            brand assets, including logos, brand colors, and social media links,
            ensuring a consistent look and feel across all your pages and
            marketing tools.
          </p>
        </header>

        {/* Logo Section */}
        <section id="logo" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">Logo</h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-3">
              Your logo is the centerpiece of your brand. Xpage lets you upload
              a custom logo or generate one using AI to ensure optimal display
              on your landing page.
            </p>

            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              Key Guidelines:
            </h3>
            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li>
                Should complement your landing page name and align with your
                primary brand color.
              </li>
              <li>Supported formats: HEIC, WEBP, SVG, PNG, JPG.</li>
              <li>Recommended size: at least 512 × 512 pixels.</li>
            </ul>

            <h3 className="text-sm font-semibold text-systemTitle mb-2 mt-4">
              Upload or Generate a Logo:
            </h3>

            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li className="mb-2">
                <span className="font-medium">
                  Log in to your Xpage admin and go to Settings → Business Info.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  In the Brand Assets section, click Manage.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">Under Logos:</span>
                <ul className="list-disc list-inside ml-6 mt-1 space-y-1 text-system600">
                  <li>
                    Upload your logo: Choose an image from your media library or
                    upload a new one.
                  </li>
                  <li>
                    Generate with AI: Let Xpage's AI create a custom logo with
                    matching brand colors.
                  </li>
                </ul>
              </li>
              <li className="mb-2">
                <span className="font-medium">Click Save.</span>
              </li>
            </ol>

            {/* Tip Box */}
            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 mt-3">
              <p className="text-sm text-systemLabel">
                <span className="font-semibold">TIP:</span> Xpage will prompt
                you if the uploaded logo doesn't meet format or resolution
                requirements. Only one logo can be active at a time, but you can
                replace or delete it whenever needed.
              </p>
            </div>
          </div>
        </section>

        {/* Brand Colors Section */}
        <section id="brand-colors" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Brand Colors
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-3">
              Consistent brand colors help reinforce recognition and create a
              polished, professional look. Xpage allows you to configure:
            </p>

            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li>
                <strong>Primary Color:</strong> Main color used across your
                landing page.
              </li>
              <li>
                <strong>Secondary Color:</strong> Complementary color for
                accents.
              </li>
              <li>
                <strong>Contrast Color:</strong> Used for readability and
                highlighting important elements.
              </li>
            </ul>

            <p className="text-sm text-systemLabel mt-3">
              Setting these colors ensures your landing page maintains a
              cohesive visual identity across every section, button, and banner.
            </p>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#logo"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "logo"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Logo
              </a>
            </li>
            <li>
              <a
                href="#brand-colors"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "brand-colors"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Brand Colors
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ManageLandingPageBrandAssets;
